import {
  ArrowRight,
  BarChart3,
  Camera,
  CheckCircle2,
  CircleDollarSign,
  Play,
  QrCode,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { formatScans, formatUsd, scanPacks, subscriptionPlan } from '../lib/pricing';

const useCases = ['Retail posters', 'Packaging', 'Billboards', 'Flyers', 'Event signage', 'Transit media'];
const steps = [
  { title: 'Scan', copy: 'A QR or public scan URL opens the ADarveo viewer instantly.', Icon: QrCode },
  { title: 'Experience', copy: 'The viewer loads the marker, video, CTA, and future WebAR target.', Icon: Camera },
  { title: 'Measure', copy: 'Views, video plays, and CTA clicks flow into the dashboard.', Icon: BarChart3 }
];

export const Home = () => (
  <main className="bg-background text-foreground">
    <section className="hero-stage overflow-hidden text-white">
      <SiteHeader theme="dark" />

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-10 md:grid-cols-[0.95fr_1.05fr] md:pb-24">
        <div className="max-w-3xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/14 bg-white/7 px-4 py-2 text-sm text-white/78">
            <Sparkles className="h-4 w-4 text-blue-300" />
            Offline media, now observable
          </div>
          <h1 className="font-heading text-5xl font-semibold leading-tight sm:text-6xl lg:text-7xl">
            Bring static ads to life with AR.
          </h1>
          <p className="mt-6 max-w-2xl text-xl leading-8 text-white/74">
            ADarveo turns posters, packaging, flyers, and billboards into scan-triggered experiences
            with measurable views, video plays, and CTA clicks.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white transition hover:bg-blue-500"
              href="/request-access"
            >
              Request access
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 px-5 py-3 font-semibold text-white transition hover:border-white/45"
              href="#how-it-works"
            >
              <Play className="h-4 w-4" />
              See the flow
            </a>
          </div>
        </div>

        <div className="relative min-h-[520px]">
          <div className="poster-scene absolute left-0 top-8 h-[470px] w-[72%] rounded-lg border border-white/16 p-6">
            <div className="flex h-full flex-col justify-between rounded-md border border-white/12 bg-black/35 p-6">
              <Logo variant="transparent" className="h-12 w-fit" />
              <div>
                <p className="text-sm uppercase text-blue-200">Mall activation</p>
                <h2 className="mt-3 font-heading text-4xl font-semibold">A poster that performs.</h2>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-xs text-white/66">
                <span className="rounded border border-white/12 py-3">Scan</span>
                <span className="rounded border border-white/12 py-3">Watch</span>
                <span className="rounded border border-white/12 py-3">Click</span>
              </div>
            </div>
          </div>
          <div className="phone-frame absolute bottom-0 right-0 h-[430px] w-[230px] rounded-[2rem] border border-white/15 p-3">
            <div className="scan-grid relative h-full overflow-hidden rounded-[1.5rem] bg-[#061325]">
              <div className="absolute inset-x-6 top-8 rounded-md bg-white/12 p-4 backdrop-blur">
                <div className="h-32 rounded bg-blue-400/30" />
                <div className="mt-4 h-3 w-28 rounded bg-white/70" />
                <div className="mt-2 h-3 w-20 rounded bg-white/36" />
              </div>
              <div className="absolute bottom-8 left-6 right-6 rounded-md bg-primary px-4 py-3 text-center text-sm font-semibold">
                Book a demo
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="how-it-works" className="mx-auto grid max-w-7xl gap-8 px-5 py-20 md:grid-cols-3">
      {steps.map(({ title, copy, Icon }) => (
        <article key={title} className="rounded-lg border border-border bg-card p-6">
          <Icon className="h-7 w-7 text-primary" />
          <h2 className="mt-5 font-heading text-2xl font-semibold">{title}</h2>
          <p className="mt-3 leading-7 text-muted">{copy}</p>
        </article>
      ))}
    </section>

    <section className="bg-background px-5 py-20" aria-labelledby="home-pricing-title">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">Simple pricing</p>
          <h2 id="home-pricing-title" className="mt-3 font-heading text-4xl font-semibold leading-tight">
            Start with the software. Add scans as campaigns grow.
          </h2>
          <p className="mt-5 leading-8 text-muted">
            One monthly subscription includes 500 scans. One-time scan packs provide clear volume
            discounts without changing your subscription.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[0.84fr_1.16fr]">
          <article className="rounded-xl bg-[#000210] p-7 text-white shadow-2xl shadow-blue-950/10 sm:p-9">
            <div className="flex items-center justify-between gap-4">
              <p className="font-heading text-xl font-semibold">{subscriptionPlan.name}</p>
              <CircleDollarSign className="h-7 w-7 text-blue-300" />
            </div>
            <div className="mt-8 flex items-end gap-2">
              <span className="font-heading text-6xl font-semibold">{formatUsd(subscriptionPlan.monthlyPriceUsd)}</span>
              <span className="pb-2 text-white/58">USD / month</span>
            </div>
            <p className="mt-4 text-lg text-white/76">
              {formatScans(subscriptionPlan.includedScans)} scans included in every monthly billing cycle.
            </p>
            <ul className="mt-8 space-y-3 text-white/72">
              {['Campaign and asset management', 'Browser-based AR viewer', 'Engagement analytics'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-300" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              className="mt-9 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white transition hover:bg-blue-500"
              to="/pricing"
            >
              View full pricing
              <ArrowRight className="h-4 w-4" />
            </Link>
          </article>

          <article className="rounded-xl border border-border bg-white p-7 sm:p-9">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">Popular scan packs</p>
            <h3 className="mt-3 font-heading text-3xl font-semibold">Volume pricing when you need it</h3>
            <div className="mt-7 divide-y divide-border border-y border-border">
              {scanPacks
                .filter((pack) => [1_000, 10_000, 50_000].includes(pack.scans))
                .map((pack) => (
                  <div key={pack.scans} className="grid grid-cols-[1fr_auto] items-center gap-5 py-5 sm:grid-cols-3">
                    <div>
                      <p className="font-heading text-xl font-semibold">{formatScans(pack.scans)} scans</p>
                      {'popular' in pack && pack.popular ? (
                        <span className="mt-1 inline-block text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                          Popular
                        </span>
                      ) : null}
                    </div>
                    <p className="hidden text-center text-muted sm:block">
                      ${(pack.priceUsd / pack.scans).toFixed(2)} per scan
                    </p>
                    <p className="text-right font-heading text-2xl font-semibold">{formatUsd(pack.priceUsd)}</p>
                  </div>
                ))}
            </div>
            <p className="mt-6 text-sm leading-6 text-muted">
              Scan packs are one-time purchases for active subscribers. Prices are shown in USD;
              applicable taxes are calculated at checkout.
            </p>
          </article>
        </div>
      </div>
    </section>

    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 md:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="text-sm font-semibold uppercase text-primary">Built for agencies and brands</p>
          <h2 className="mt-3 font-heading text-4xl font-semibold leading-tight">
            Make offline advertising measurable.
          </h2>
          <p className="mt-5 leading-8 text-muted">
            Organize work by campaign and ad. Share a scan URL, generate QR codes,
            send visitors to the right destination, and see where the physical creative is earning attention.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {useCases.map((item) => (
            <div key={item} className="flex items-center gap-3 rounded-md border border-border bg-background p-4">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <span className="font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#000210] px-5 py-20 text-white">
      <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        <div>
          <p className="text-sm font-semibold uppercase text-blue-200">ADarveo Pro</p>
          <h2 className="mt-3 font-heading text-4xl font-semibold">Launch measurable AR campaigns.</h2>
        </div>
        <a
          className="inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 font-semibold text-[#000210] transition hover:bg-blue-50"
          href="/request-access"
        >
          Tell us about your project
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
    <SiteFooter />
  </main>
);
